import React, { useState } from 'react';
import { Star, Save, CheckCircle, AlertTriangle } from 'lucide-react';
import { API_BASE, authFetch } from '../pages/admin/constants';

export interface UpdateNotice {
  enabled: boolean;
  kind?: 'new' | 'updated';
  date?: string;
  summary?: string;
}

interface AgentWhatsNewEditorProps {
  agentId: string;
  agentName: string;
  initialNotice?: UpdateNotice;
  version?: string;
  /** Called after a successful save with the persisted notice. Lets callers
   *  (e.g. the inline chat editor) refresh the intro banner instantly. */
  onSaved?: (notice: UpdateNotice) => void;
}

/**
 * Scoped "What's New" editor for the per-agent dashboard.
 *
 * Agent-admins (and super-admins) can toggle the home-screen update notice and
 * edit the dated "What's New" blurb shown in the agent's intro message. It saves
 * via the narrow `PUT /admin/registry/:agentId/whats-new` endpoint
 * (requireAgentAdmin), which can ONLY touch the update notice + an optional
 * version-history entry — never the rest of the registry.
 */
const AgentWhatsNewEditor: React.FC<AgentWhatsNewEditorProps> = ({
  agentId,
  agentName,
  initialNotice,
  version,
  onSaved,
}) => {
  const [enabled, setEnabled] = useState<boolean>(!!initialNotice?.enabled);
  const [date, setDate] = useState<string>(initialNotice?.date || new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState<string>(initialNotice?.summary || '');
  const [recordInHistory, setRecordInHistory] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // Preserve the notice `kind` (NEW vs UPDATED) set elsewhere (e.g. the
      // super-admin quick control on the home-screen version chit) so editing
      // the blurb here doesn't silently flip the badge type.
      const kind = initialNotice?.kind || 'updated';
      const body: Record<string, unknown> = {
        updateNotice: { enabled, kind, date, summary },
      };
      if (recordInHistory && summary.trim()) {
        body.versionHistoryEntry = { version: version || '', date, notes: summary.trim() };
      }
      const response = await authFetch(`${API_BASE}/admin/registry/${agentId}/whats-new`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Save failed: ${response.status}`);
      }
      setSuccess("What's New saved. Users will see the update on the home screen until they next open this agent.");
      setTimeout(() => setSuccess(null), 5000);
      onSaved?.({ enabled, kind, date, summary });
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Star size={18} />
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>What's New — {agentName}</h2>
      </div>
      <p style={{ fontSize: '0.85rem', color: '#6f747b', marginTop: 0 }}>
        The <strong>What's New</strong> text below appears as a dated banner in this agent's intro
        message whenever it's filled in — clear it to remove the banner. Optionally check{' '}
        <em>Announce update on home screen</em> to <strong>also</strong> show an{' '}
        <strong>UPDATED</strong> badge and float the agent card to the top of the home screen for
        every user until they next open it.
      </p>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', margin: '8px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span>Announce update on home screen</span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, alignItems: 'start', marginTop: 8 }}>
        <div>
          <label style={{ fontSize: '0.8rem', color: '#6f747b', display: 'block', marginBottom: 4 }}>Status date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #cfd2d6', borderRadius: 6 }}
          />
        </div>
        <div>
          <label style={{ fontSize: '0.8rem', color: '#6f747b', display: 'block', marginBottom: 4 }}>
            What's New (shown in the intro message; Markdown ok)
          </label>
          <textarea
            rows={5}
            placeholder={'e.g. Added 200 new documents covering billing automation, price increases, and cash application.'}
            value={summary}
            onChange={e => setSummary(e.target.value)}
            style={{ width: '100%', padding: '8px', fontSize: '0.9rem', border: '1px solid #cfd2d6', borderRadius: 6, resize: 'vertical' }}
          />
        </div>
      </div>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', margin: '10px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={recordInHistory} onChange={e => setRecordInHistory(e.target.checked)} />
        <span>Also record this in the version history{version ? ` (v${version})` : ''}</span>
      </label>

      <div style={{ marginTop: 12 }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', fontSize: '0.9rem', fontWeight: 600,
            background: '#00B194', color: '#fff', border: 'none', borderRadius: 6,
            cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          <Save size={16} /> {saving ? 'Saving…' : "Save What's New"}
        </button>
      </div>

      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: '#047857', fontSize: '0.9rem' }}>
          <CheckCircle size={16} /> {success}
        </div>
      )}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: '#b91c1c', fontSize: '0.9rem' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}
    </div>
  );
};

export default AgentWhatsNewEditor;
